// Phase 36: i18n — React provider + hooks. Wraps the pure helpers in
// i18n/core.js (which is JSX-free so Node can import it directly from
// tests).
//
// Architecture:
//   - `<LanguageProvider lang>` re-renders when lang changes. Call
//     `setLang(newLang)` to switch.
//   - All user-facing strings should go through `useI18n().t(key, vars)`.
//   - Strings intentionally NOT translated: user-entered data (category
//     names, notes, tags). Only the hard-coded UI is.

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { resolve, LOCALES, FALLBACK_LOCALE } from './core.js';

// Re-export so existing imports of i18n/index.jsx (App.jsx, etc.)
// don't need to change.
export { resolve, LOCALES, FALLBACK_LOCALE };

const LanguageContext = createContext({
  lang: FALLBACK_LOCALE,
  setLang: () => {},
  t: (key, vars, defaultValue) => resolve(key, FALLBACK_LOCALE, vars, defaultValue),
});

export function LanguageProvider({ lang, setLang, children }) {
  const value = useMemo(() => {
    const t = (key, vars, defaultValue) => resolve(key, lang, vars, defaultValue);
    // Expose lang to non-React code (TrendChart's Intl-based monthLabel)
    // without forcing them through React Context.
    if (typeof window !== 'undefined') {
      window.__financeAppLang__ = lang;
      document.documentElement.lang = lang;
    }
    return { lang, setLang, t };
  }, [lang, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  return useContext(LanguageContext);
}

// Convenience hook — returns the active lang plus the switcher.
export function useLanguage() {
  const { lang, setLang } = useContext(LanguageContext);
  const switchTo = useCallback((code) => {
    if (LOCALES.some(l => l.code === code)) setLang(code);
  }, [setLang]);
  return { lang, setLang: switchTo };
}
